import { useEffect, useState, type Ref } from 'react'
import { Check, Loader2, RotateCw } from 'lucide-react'
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/shared/components/ui/command'
import { CHAT_COPY, useChatDirectory, type DirectoryEntry } from '@/entities/chat'
import { PersonAvatar } from './PersonAvatar'

export function PersonPicker({
  selectedIds,
  excludedIds,
  onPick,
  autoFocus,
  disabled,
  pendingUserId,
  inputRef,
}: {
  selectedIds: number[]
  excludedIds: number[]
  onPick: (entry: DirectoryEntry) => void
  autoFocus?: boolean
  disabled?: boolean
  pendingUserId?: number | null
  inputRef?: Ref<HTMLInputElement>
}) {
  const [search, setSearch] = useState('')
  const directoryQuery = useChatDirectory(search, true)

  useEffect(() => {
    void directoryQuery.refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const entries = (directoryQuery.data ?? []).filter((entry) => !excludedIds.includes(entry.userId))

  return (
    <Command shouldFilter={false} className="rounded-md border border-line">
      <CommandInput
        ref={inputRef}
        autoFocus={autoFocus}
        value={search}
        onValueChange={setSearch}
        placeholder={CHAT_COPY.newChatSearch}
      />
      <CommandList className="max-h-[220px]">
        {directoryQuery.isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-4 w-4 animate-spin text-ink-4" />
          </div>
        ) : directoryQuery.isError ? (
          <button
            type="button"
            onClick={() => void directoryQuery.refetch()}
            className="flex w-full items-center justify-center gap-1.5 px-3 py-6 text-[12.5px] text-ink-3 hover:bg-hover"
          >
            <RotateCw className="h-3.5 w-3.5" />
            {CHAT_COPY.directoryFailed}
          </button>
        ) : (
          <>
            <CommandEmpty>{search.trim() === '' ? CHAT_COPY.newChatEmptyNone : CHAT_COPY.newChatEmpty}</CommandEmpty>
            {entries.map((entry) => (
              <CommandItem
                key={entry.userId}
                value={String(entry.userId)}
                disabled={disabled}
                onSelect={() => onPick(entry)}
              >
                <PersonAvatar name={entry.name} photoUrl={entry.photoUrl} photoFileId={entry.photoFileId} size="sm" />
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate">{entry.name}</span>
                  <span className="truncate text-[11.5px] text-ink-4">{entry.employeeType}</span>
                </div>
                {pendingUserId === entry.userId ? (
                  <Loader2 className="ml-auto h-3.5 w-3.5 animate-spin text-ink-4" />
                ) : (
                  selectedIds.includes(entry.userId) && (
                    <Check className="ml-auto h-3.5 w-3.5 shrink-0 text-brand-accent-ink" />
                  )
                )}
              </CommandItem>
            ))}
          </>
        )}
      </CommandList>
    </Command>
  )
}
